import React, { FC, useCallback, useEffect, useState } from "react";
import { Box, Button, makeStyles, createStyles, Grid } from "@material-ui/core";
import { useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router";

import { InputContainer } from "modus/components/form/input-container/InputContainer";
import { TextAreaContainer } from "modus/components/form/textarea-container/TextAreaContainer";
import ActionInliner from "legacy-components/form/action/ActionInliner";
import FormAlert from "legacy-components/form/container/FormAlert";
import { GrantTypesAppCreationForm } from "libs/resources/identity/applications/application-form/GrantTypeAppCreationForm";
import ApiService from "../services/ApiService";
import { MY_SELECTED_TEAM_ } from "header/team-selector/TeamSelector";
import { resolveAPIErrorMessage } from "modus/components/error-message/ErrorMessage";
import Alert from "@material-ui/lab/Alert";
import { useAsyncAction } from "modus/components/form/async-actions/AsyncAction";

interface ResourceAppBasicFormProps {
  close: () => void;
  refresh: () => void;
  open: boolean;
}

const useStyles = makeStyles(() =>
  createStyles({
    formLayout: {
      display: "flex",
      width: "100%",
    },
    formContainer: {
      position: "relative",
      display: "flex",
      flexDirection: "column",
      width: "100%",
    },
    submitLoader: {
      position: "absolute",
      zIndex: 2,
      width: "100%",
      height: "100%",
      backgroundColor: "rgb(255, 255, 255, 0.32)",
    },
  })
);

const ResourceAppBasicForm: FC<ResourceAppBasicFormProps> = (
  {close, refresh, open} 
) => {
  const currentTeam = useSelector(
    (state: any) =>
      state?.teamManager?.teams?.currentTeam?.teamName ??
      sessionStorage.getItem(MY_SELECTED_TEAM_)
  );

  const intl = useIntl();
  const styles = useStyles();
  const [success, setSuccess] = useState(false);
  const [loader, setLoader] = useState(false);
  const [grantTypes, setGrantTypes] = useState<string[]>([]);
  const [successProcessed, setSuccessProcessed] = useState(false);
  const history = useHistory();
  const [logoutUri, setLogoutUri] = useState<string[]>([]);
  const [redirectUri, setRedirectUri] = useState<string[]>([]);
  const [error, setError] = useState({
    is_error: false,
    message: "",
  });

  const identityEnvironment = useSelector(
      (state: any) => state?.common?.identityEnvironment
    );

  const onGrantTypeChange = useCallback(
    (grantTypeList: string[]) => {
      setGrantTypes(grantTypeList);
    },
    [setGrantTypes]
  );

  const onLogoutUriChange = useCallback(
    (logoutUri: string[]) => {
      setLogoutUri(logoutUri);
    },
    [setGrantTypes]
  );

  const onRedirectUriChange = useCallback(
    (redirectUri: string[]) => {
      setRedirectUri(redirectUri);
    },
    [setGrantTypes]
  );

  const [formValues, setFormValues] = useState({
    name: "",
    displayName: "",
    description: "",
    basePath: "",
    applicationName: "",
    type: false,
  });
  const [hasFieldError2, setHasFieldError2] = useState(false); // New state to track child component validation error
  const [hasFieldError3, setHasFieldError3] = useState(false); // New state to track child component validation error

  const handleSetError2 = (value: boolean) => {
    setHasFieldError2(value); // Track error from child component
  };
  const handleSetError3 = (value: boolean) => {
    setHasFieldError3(value); // Track error from child component
  };

  const FormErrorViewer = ({ errors }: { errors?: string[] }) => {
    return (
      <>
        {errors?.map((error, index) => (
          <div key={index}>{error}</div>
        ))}
      </>
    );
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormValues({
      ...formValues,
      [name]: type === "checkbox" ? checked : value,
    });
  };


  function createBasepath(basepath: string) {
    if(basepath.startsWith("/")) {
      return basepath;
    }
    return `/${basepath}`;
  }

  const {state,  subscribe } = useAsyncAction(() =>
    ApiService.createApi(currentTeam?.id, {
      applicationType: "RESOURCE_SERVER",
      name: formValues.applicationName,
      displayName: formValues.displayName,
      description: formValues.description,
      basePath: createBasepath(formValues.basePath),
      grantTypes: grantTypes,
      redirectUri: redirectUri,
      logoutUri: logoutUri,
    })
  );

  useEffect(() => {
    if (!state?.loaded || !open || !refresh || !close || successProcessed) {
      return;
    }
    if(state.error){
      return
    }
    if (state?.loaded) {
      if (open) {
        close();
      }
      refresh();
      if (!state?.error) {
      const appNameForUrl = encodeURIComponent(formValues.applicationName.trim());
        history.push(`/home/apis/${state.value?.applicationId}/gateway/${identityEnvironment}/summary?apiName=${encodeURIComponent(appNameForUrl)}`, state.value?.applicationId);
      }
    }
  }, [state.loaded, open, close, refresh]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessProcessed(false); 
    subscribe();
  };

  return (
    <form noValidate autoComplete="off" onSubmit={handleSubmit}>
      <Box style={{ padding: "3px" }}>
        {state?.error && (
          <Alert severity="error" style={{ width: "100%" }}>
             {resolveAPIErrorMessage(state.error)}
          </Alert>
        )}
        {loader && <Box className={styles.submitLoader}></Box>}
        {error.is_error && (
          <FormAlert severity="error">
            <FormErrorViewer errors={[error.message]} />
          </FormAlert>
        )}
        {success && (
          <FormAlert severity="success">
            {"Application created Successfully"}
          </FormAlert>
        )}
        <Grid container>
          <Grid item md={6} sm={12} style={{ order: 1 }}>
            <Box pr={1}>
              <InputContainer
                label={intl.formatMessage({
                  defaultMessage: "Display Name",
                })}
                placeholder={intl.formatMessage({
                  defaultMessage: "Enter the Application Name",
                })}
                value={formValues.displayName}
                onChange={handleChange}
                name={"displayName"}
                validate={{
                  required: true,
                  minLength: 1,
                  maxLength: 100,
                  allowSpace: true,
                  pattern: "^[a-zA-Z0-9-_ ]+$",
                }}
                setError={handleSetError2}
                required
              ></InputContainer>

              <InputContainer
                label={intl.formatMessage({
                  defaultMessage: "Basepath",
                })}
                placeholder={intl.formatMessage({
                  defaultMessage: "Enter the Base Path",
                })}
                value={
                  formValues.basePath
                }
                onChange={handleChange}
                name={"basePath"}
                validate={{
                  required: true,
                  minLength: 3,
                  maxLength: 64,
                  allowSpace: false,
                  pattern: "^(?!.*//)(?!.*__)[a-zA-Z0-9/_-]+$"
                }}
                setError={handleSetError2}
                required
              ></InputContainer>

              <InputContainer
                label={intl.formatMessage({
                  defaultMessage: "Aplication Name / Scope Prefix",
                })}
                placeholder={intl.formatMessage({
                  defaultMessage: "Enter the ApplicationId/Scope Prefix",
                })}
                value={formValues.applicationName}
                onChange={handleChange}
                name={"applicationName"}
                validate={{
                  required: true,
                  minLength: 1,
                  maxLength: 100,
                  allowSpace: false
                }}
                setError={handleSetError2}
                required
              ></InputContainer>
            </Box>
          </Grid>

          <GrantTypesAppCreationForm
            onGrantTypeUpdate={onGrantTypeChange}
            onLogoutUriChange={onLogoutUriChange}
            onRedirectUriChange={onRedirectUriChange}
          />

          <Grid item style={{ order: 3 }} sm={12}>
            <TextAreaContainer
              label={"Description"}
              placeholder={"Enter the details about application for reference"}
              value={formValues.description}
              name={"description"}
              onChange={handleChange}
              required
              validate={{
                minLength:1,
                maxLength: 255,
                // Allows letters, numbers, punctuation, spaces, underscores, hyphens, and slashes
                pattern: "^[\\u0000-\\u00FF\\s.!?'()_\\-/]*$",
              }}
              setError={handleSetError3}
            ></TextAreaContainer>
          </Grid>
        </Grid>

        <ActionInliner padding="inset-top">
          <Button
            onClick={close}
            type="button"
            variant="outlined"
            disableElevation
          >
            {" "}
            <FormattedMessage defaultMessage="Cancel" />
          </Button>

          <Button
            onSubmit={handleSubmit}
            type="submit"
            variant="contained"
            color="primary"
            disableElevation
            disabled={
              state?.loading ||
              hasFieldError2 ||
              !formValues.displayName ||
              !formValues.basePath ||
              !formValues.applicationName ||
              !formValues.description ||
              grantTypes.length === 0 || 
              (grantTypes.includes("authorization_code") &&
                (logoutUri.length === 0 || redirectUri.length === 0))
            }
          >
            <FormattedMessage defaultMessage="Create" />
          </Button>
        </ActionInliner>
        <Box pb={2} />
      </Box>
    </form>
  );
};

export default ResourceAppBasicForm;
